import {
  BreadTransaction,
  FirestoreDocCategory,
  FirestoreDocCategoryGroup,
} from "breadcommon";
import {
  categoryGroupColor,
  COLORED_TYPE_BY_CATEGORY_TYPE,
} from "../common/categoryTypeColor";
import type { GroupedBarGroup, GroupedBarRow } from "../common/groupedBarRows";
import type {
  BreakdownType,
  CategoryBreakdownData,
} from "./useCategoryBreakdownData";

/** One row of the grouped bars: a group, or a category inside one. */
export interface BreakdownBarRow extends GroupedBarRow {
  /** The plain signed sum of the row's transactions. */
  net: number;
  currency: string;
  /** Which type's color to draw the bar in; null when there's nothing to draw. */
  color: BreakdownType | null;
  transactions: BreadTransaction[];
}

export type BreakdownBarGroup = GroupedBarGroup<BreakdownBarRow>;

/**
 * Every category, grouped and in category-list order, whether or not it had
 * any transactions. `groupedCategories` supplies that order and already leaves
 * out Silent categories and empty groups.
 *
 * Groups can mix category types, so a group's total is the plain signed sum of
 * all its categories, and its color follows budget tracking's rule: a
 * single-type group takes its type's color; a mixed group is expenses-colored
 * when it nets negative, and otherwise income- or savings-colored, whichever
 * brought in more.
 *
 * Narrowed to one `type`, only that type's categories are shown, a group's
 * total covers just those, and groups without any are left out.
 */
export function buildBreakdownBars(
  data: CategoryBreakdownData,
  groupedCategories: Map<string, Map<string, FirestoreDocCategory>>,
  categoryGroups: Map<string, FirestoreDocCategoryGroup>,
  type: BreakdownType | "all" = "all",
): BreakdownBarGroup[] {
  return Array.from(groupedCategories.entries()).flatMap(
    ([groupId, allCategories]): BreakdownBarGroup[] => {
      const categories = Array.from(allCategories.values()).filter(
        (category) =>
          type === "all" ||
          COLORED_TYPE_BY_CATEGORY_TYPE[category.type] === type,
      );
      if (categories.length === 0) return [];

      const categoryRows: BreakdownBarRow[] = categories.map((category) => {
        const total = data.categoryTotals.get(category.id);
        return {
          id: category.id,
          kind: "category",
          groupId,
          label: category.emoji
            ? `${category.emoji} ${category.name}`
            : category.name,
          net: total?.net ?? 0,
          color: COLORED_TYPE_BY_CATEGORY_TYPE[category.type] ?? null,
          currency: data.currency,
          transactions: total?.transactions ?? [],
        };
      });

      const net = categoryRows.reduce((sum, row) => sum + row.net, 0);
      const transactions = categoryRows.flatMap((row) => row.transactions);
      return [
        {
          id: groupId,
          kind: "group",
          groupId,
          label: categoryGroups.get(groupId)?.name ?? "Ungrouped",
          net,
          currency: data.currency,
          color: categoryGroupColor(categories, net, transactions),
          transactions,
          categories: categoryRows,
        },
      ];
    },
  );
}
